import React, { FC, useCallback, useMemo } from 'react';
import { bmisActions, useBmisDispatch, useBmisState } from '@packages/contexts/bmis';
import { BmisBatteryBadge } from '@web/bmis/components/bmis-battery-badge';
import { BmisPDF } from './bmis-pdf';
import { Button, Icon, Typography } from '@packages/ui/shared';
import { DATE_FORMAT } from '@web/config/constants';
import { getDisplayDate } from '@web/utils';
import { StyleSheet } from 'react-native';
import { useAuthState } from '@packages/contexts/auth';
import { useNavigate, useParams } from 'react-router-dom';
import { usePrint } from '@web/components/printable-container';

const styles = StyleSheet.create({
    typographyStyles: {
        fontSize: 21,
        fontWeight: '600',
        lineHeight: 32,
    },
    button: {
        height: 40,
        width: 40,
    },
});

export const BmisHeader: FC = () => {
    const bmisDispatch = useBmisDispatch();
    const navigate = useNavigate();
    const { account } = useAuthState();

    const { vin: vinString } = useParams<{ vin: string }>();
    const { selectedBmisRow } = useBmisState();

    const formattedServiceDate = useMemo(
        () => getDisplayDate(selectedBmisRow?.serviceDateandTime, DATE_FORMAT.MON_DAY_YEAR),
        [selectedBmisRow?.serviceDateandTime]
    );

    const printableDocumentName = useMemo(() => {
        return `BMIS_Report_${vinString}_${getDisplayDate(new Date(), DATE_FORMAT.YYYY_MM_DD)}`;
    }, [vinString]);

    const { componentRef, print, isPrintLoading } = usePrint({ documentTitle: printableDocumentName });

    const handleButtonPress = useCallback(() => {
        if (window.history.state && window.history.length > 1) {
            navigate(-1);
        } else {
            navigate(`/vin/${vinString}`);
        }
        bmisDispatch(bmisActions.setRowGuid({ rowGuid: null }));
    }, [bmisDispatch, navigate, vinString]);

    const allowPrint = useMemo(() => {
        return account?.bmisCapabilities?.canDownloadFullBatteryReport;
    }, [account?.bmisCapabilities?.canDownloadFullBatteryReport]);

    return (
        <>
            <div className="d-flex align-items-center">
                <div className="mr-4">
                    <Button variant="ghost-blue" style={styles.button} onPress={handleButtonPress}>
                        <Icon name="arrow-back" color="brandSecondary" size={24} />
                    </Button>
                </div>
                <div className="mb-0 mr-4">
                    <Typography variant="h3" style={styles.typographyStyles}>
                        {formattedServiceDate}
                    </Typography>
                </div>
                <BmisBatteryBadge label={selectedBmisRow?.finalBatteryTestDecision} />
            </div>

            {allowPrint && (
                <>
                    <div className="d-flex align-items-center">
                        <div className="ml-6">
                            <Button
                                variant="ghost-blue"
                                disabled={isPrintLoading}
                                style={styles.button}
                                onPress={print}
                            >
                                <Icon name="sync-down" color="blueOne" size={24} />
                            </Button>
                        </div>
                    </div>

                    <BmisPDF componentRef={componentRef} isPrintLoading={isPrintLoading} />
                </>
            )}
        </>
    );
};
